import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    InputAdornment,
    CircularProgress,
    Alert,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    Fade,
    useTheme,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Lock as LockIcon,
    Person as PersonIcon,
    Visibility,
    VisibilityOff,
    Phone as PhoneIcon,
    Email as EmailIcon,
} from '@mui/icons-material';
import {
    signInWithPhoneNumber,
    RecaptchaVerifier,
    signOut,
    sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from '../../firebase/firebaseConfig';
import { loginUser } from './authSlice';
import { setNeedsOnboarding, setTrialEnd } from './onboardingSlice';
import { authenticatedFetch, publicFetch } from '../../utils/authApi';
import ReviewCarousel from './ReviewCarousel';

const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:5001'
    : 'https://policytrackr-be-187769890360.asia-south1.run.app');

const LOGIN_MODE_KEY = 'policytrackr_login_mode';

const MobileUnifiedLogin = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();

    // Login mode: 'admin' or 'staff'
    const [loginMode, setLoginMode] = useState(() => {
        const savedMode = localStorage.getItem(LOGIN_MODE_KEY);
        return savedMode === 'staff' ? 'staff' : 'admin';
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [hasNavigated, setHasNavigated] = useState(false);

    // Admin OTP states
    const [phoneNumber, setPhoneNumber] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [isPhoneValid, setIsPhoneValid] = useState(true);
    const [otp, setOtp] = useState('');
    const [otpStep, setOtpStep] = useState('phone'); // 'phone' or 'otp'
    const [confirmationResult, setConfirmationResult] = useState(null);

    // Staff login states
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [isEmailValid, setIsEmailValid] = useState(true);
    const [passwordError, setPasswordError] = useState('');
    const [isPasswordValid, setIsPasswordValid] = useState(true);

    // Forgot password states
    const [isForgotPassword, setIsForgotPassword] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetSuccess, setResetSuccess] = useState(false);

    // Override body background color for this page (fix white status bar area)
    useEffect(() => {
        document.body.classList.add('blue-bg');
        return () => {
            document.body.classList.remove('blue-bg');
        };
    }, []);

    // Initialize reCAPTCHA when in admin mode
    useEffect(() => {
        if (loginMode === 'admin') {
            try {
                if (!window.recaptchaVerifier) {
                    window.recaptchaVerifier = new RecaptchaVerifier(
                        'recaptcha-container',
                        {
                            size: 'invisible',
                            callback: () => { },
                            'expired-callback': () => {
                                setError('Security verification expired. Please try again.');
                                if (window.recaptchaVerifier) {
                                    window.recaptchaVerifier.clear();
                                    window.recaptchaVerifier = null;
                                }
                            }
                        },
                        auth
                    );
                }
            } catch (err) {
                console.error('Error initializing reCAPTCHA:', err);
            }
        }

        return () => {
            if (window.recaptchaVerifier && loginMode !== 'admin') {
                try {
                    window.recaptchaVerifier.clear();
                } catch (err) {
                    console.error('Error clearing recaptcha:', err);
                }
                window.recaptchaVerifier = null;
            }
        };
    }, [loginMode]);

    // Handle login mode toggle
    const handleModeChange = (event, newMode) => {
        if (newMode !== null) {
            setLoginMode(newMode);
            localStorage.setItem(LOGIN_MODE_KEY, newMode);
            setError('');
            setEmailError('');
            setPasswordError('');
            setPhoneError('');
            setIsEmailValid(true);
            setIsPasswordValid(true);
            setIsPhoneValid(true);
            setIsForgotPassword(false);
            setResetSuccess(false);
            setResetEmail('');
            if (newMode === 'staff') {
                setOtpStep('phone');
                setOtp('');
            }
        }
    };

    const validatePhone = (phone) => {
        const digitsOnly = phone.replace(/\D/g, '');
        return digitsOnly.length >= 10;
    };

    const handlePhoneChange = (e) => {
        // Allow numbers and initial '+' character
        const newPhone = e.target.value.replace(/[^\d+]/g, '').slice(0, 16);
        setPhoneNumber(newPhone);
        if (phoneError) {
            setPhoneError('');
            setIsPhoneValid(true);
        }
        if (error) setError('');
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleEmailChange = (e) => {
        const newEmail = e.target.value;
        setEmail(newEmail);
        if (emailError) {
            setEmailError('');
            setIsEmailValid(true);
        }
        if (error) setError('');

        if (newEmail === '') {
            setEmailError('');
            setIsEmailValid(true);
        } else if (!validateEmail(newEmail)) {
            setEmailError('Please enter a valid email address');
            setIsEmailValid(false);
        } else {
            setEmailError('');
            setIsEmailValid(true);
        }
    };

    const handlePasswordChange = (e) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        if (passwordError) {
            setPasswordError('');
            setIsPasswordValid(true);
        }
        if (error) setError('');
    };

    const handleStaffLogin = async (e) => {
        if (e) e.preventDefault();
        setError('');
        if (!isEmailValid || email === '') {
            setEmailError('Please enter a valid email address');
            setIsEmailValid(false);
            return;
        }
        if (!password || password.trim() === '') {
            setPasswordError('Please enter your password');
            setIsPasswordValid(false);
            return;
        }
        setLoading(true);
        try {
            const result = await dispatch(loginUser({ email, password }));
            if (result.meta.requestStatus === 'fulfilled') {
                navigate('/renewals');
            } else {
                throw result.error;
            }
        } catch (err) {
            if (err.code === 'auth/invalid-login-credentials') {
                setError('Incorrect email or password');
            } else if (err.code === 'auth/invalid-email') {
                setEmailError('Please enter a valid email address');
                setIsEmailValid(false);
            } else {
                setError(err.message || 'Login failed');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        if (e) e.preventDefault();
        setError('');
        setResetSuccess(false);
        if (!resetEmail || !validateEmail(resetEmail)) {
            setError('Please enter a valid email address');
            return;
        }
        setLoading(true);
        try {
            await sendPasswordResetEmail(auth, resetEmail);
            setResetSuccess(true);
            setError('');
        } catch (err) {
            console.error('Error sending reset email:', err);
            if (err.code === 'auth/user-not-found') {
                setError('No user found with this email address');
            } else {
                setError(err.message || 'Failed to send reset email');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSendOtp = async (e) => {
        if (e) e.preventDefault();
        setError('');
        if (!phoneNumber || phoneNumber.trim() === '') {
            setPhoneError('Please enter your phone number');
            setIsPhoneValid(false);
            return;
        }
        if (!validatePhone(phoneNumber)) {
            setPhoneError('Please enter a valid 10-digit phone number');
            setIsPhoneValid(false);
            return;
        }
        setLoading(true);
        try {
            // Use existing + if present, otherwise default to +91
            let formattedPhone = phoneNumber.startsWith('+') ? phoneNumber : `+91${phoneNumber}`;
            const response = await publicFetch(`${API_BASE_URL}/api/check-admin-auth`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phoneNumber: formattedPhone }),
            });
            if (!response.ok) throw new Error('Failed to verify authorization. Please try again.');
            const { authorized } = await response.json();
            if (!authorized) {
                setPhoneError('This phone number is not authorized for admin access.');
                setIsPhoneValid(false);
                setLoading(false);
                return;
            }
            if (!window.recaptchaVerifier) {
                window.recaptchaVerifier = new RecaptchaVerifier('recaptcha-container', { size: 'invisible' }, auth);
            }
            const result = await signInWithPhoneNumber(auth, formattedPhone, window.recaptchaVerifier);
            setConfirmationResult(result);
            setOtpStep('otp');
            setError('');
            setPhoneError('');
            setIsPhoneValid(true);
        } catch (err) {
            console.error('Error sending OTP:', err);
            if (err.code === 'auth/invalid-phone-number') {
                setPhoneError('Invalid phone number format.');
                setIsPhoneValid(false);
            } else if (err.code === 'auth/too-many-requests') {
                setError('Too many requests. Please try again later.');
            } else {
                setError(err.message || 'Failed to send OTP. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        if (e) e.preventDefault();
        setError('');
        if (!otp || otp.length !== 6) {
            setError('Please enter a valid 6-digit OTP');
            return;
        }
        if (!confirmationResult) {
            setError('OTP session expired. Please start over.');
            return;
        }
        setLoading(true);
        try {
            const userCredential = await confirmationResult.confirm(otp);
            const user = userCredential.user;
            const response = await authenticatedFetch(`${API_BASE_URL}/api/sync-admin-profile`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ uid: user.uid, phoneNumber: user.phoneNumber }),
            });
            if (!response.ok) throw new Error('Verification failed on server. Please try again.');
            const result = await response.json();
            if (result.success) {
                // Check if admin needs onboarding
                if (!result.onboardingComplete) {
                    dispatch(setNeedsOnboarding(true));
                }
                if (result.trialEnd) {
                    dispatch(setTrialEnd(result.trialEnd));
                }
                await new Promise(resolve => setTimeout(resolve, 1500));
                navigate('/balance');
            } else {
                await signOut(auth);
                setError('This phone number is not authorized for admin access.');
            }
        } catch (err) {
            console.error('Error during OTP verification:', err);
            if (err.code === 'auth/invalid-verification-code') {
                setError('Invalid OTP. Please check and try again.');
            } else {
                setError(err.message || 'Failed to verify OTP.');
            }
        } finally {
            setLoading(false);
        }
    };

    const textFieldSx = {
        mb: 1,
        height: '60px', // Slightly taller for better touch
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            bgcolor: '#f8fafc',
            height: '46px', // Better height for mobile
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0051C3' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0051C3', borderWidth: '2px' }
        },
        '& .MuiInputLabel-root': {
            bgcolor: 'white',
            px: 0.5,
            borderRadius: '4px',
            fontSize: '0.85rem', // Slightly larger for readability
            '&.Mui-focused': { color: '#0051C3' }
        },
        '& .MuiFormHelperText-root': { margin: '2px 0 0 0', height: '12px', fontSize: '0.7rem' }
    };

    const secondaryButtonSx = {
        color: '#0051C3',
        fontWeight: 500,
        textTransform: 'none',
        fontSize: '0.8rem', // Better readability
        height: '26px',
        '&:hover': { textDecoration: 'underline', bgcolor: 'transparent' }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#006AFF' // Blue background to cover safe area at top
        }}>
            {/* Fixed blue bar to cover status bar area */}
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 'env(safe-area-inset-top, 50px)',
                    bgcolor: '#006AFF',
                    zIndex: 9999
                }}
            />
            {/* Top section: Login Card on Blue Gradient */}
            <Box
                sx={{
                    flex: '1 1 auto',
                    minHeight: '65vh',
                    background: 'linear-gradient(to bottom, #006AFF 0%, #003785 100%)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 2,
                    pt: 'calc(env(safe-area-inset-top) + 20px)', // Account for iOS safe area
                    position: 'relative',
                    zIndex: 2,
                }}
            >
                <Fade in={true} timeout={600}>
                    <Paper
                        elevation={24}
                        sx={{
                            width: '92%',
                            maxWidth: '340px',
                            minHeight: 'auto', // Let content determine height
                            borderRadius: '18px',
                            px: { xs: 3, sm: 4 },
                            pt: 2.5, // Better top padding
                            pb: 2, // Better bottom padding
                            bgcolor: 'white',
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            overflow: 'visible' // Allow helper text to overlap if needed
                        }}
                    >
                        {/* Title - "PolicyTrackr" in blue - FIXED POSITION */}
                        <Box
                            sx={{
                                fontSize: { xs: '1.5rem', sm: '1.7rem' }, // Responsive font size
                                fontWeight: 900,
                                lineHeight: 1,
                                fontFamily: '"Roboto Slab", serif !important',
                                letterSpacing: '-1.2px',
                                color: '#0051C3 !important',
                                textAlign: 'center',
                                mb: 1.5,
                                width: '100%',
                                mt: 0.5
                            }}
                        >
                            PolicyTrackr
                        </Box>

                        <Box sx={{ height: '38px', mb: 2 }}>
                            {!isForgotPassword && (
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '38px',
                                        bgcolor: '#f1f5f9',
                                        borderRadius: '100px',
                                        p: '3px',
                                        display: 'flex',
                                        position: 'relative',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {/* Animated Pill Background */}
                                    <motion.div
                                        initial={false}
                                        animate={{ x: loginMode === 'admin' ? 0 : '100%' }}
                                        style={{
                                            position: 'absolute',
                                            top: '3px',
                                            left: '3px',
                                            width: 'calc(50% - 3px)',
                                            height: '32px',
                                            backgroundColor: 'white',
                                            borderRadius: '100px',
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                                            zIndex: 0
                                        }}
                                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                    />

                                    {/* Admin Option */}
                                    <Box
                                        onClick={() => handleModeChange(null, 'admin')}
                                        sx={{
                                            flex: 1,
                                            zIndex: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 0.5,
                                            color: loginMode === 'admin' ? '#0051C3' : '#64748b',
                                            fontWeight: 600,
                                            fontSize: '0.8rem', // Slightly larger
                                            transition: 'color 0.2s',
                                            userSelect: 'none'
                                        }}
                                    >
                                        <LockIcon sx={{ fontSize: 14 }} />
                                        Admin
                                    </Box>

                                    {/* Staff Option */}
                                    <Box
                                        onClick={() => handleModeChange(null, 'staff')}
                                        sx={{
                                            flex: 1,
                                            zIndex: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 0.5,
                                            color: loginMode === 'staff' ? '#0051C3' : '#64748b',
                                            fontWeight: 600,
                                            fontSize: '0.8rem', // Slightly larger
                                            transition: 'color 0.2s',
                                            userSelect: 'none'
                                        }}
                                    >
                                        <PersonIcon sx={{ fontSize: 14 }} />
                                        Staff
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {/* Dynamic Title & Subtitle - FIXED HEIGHT CONTAINER */}
                        <Box sx={{ textAlign: 'center', height: isForgotPassword ? '50px' : '30px', mb: 1.5 }}>
                            <Fade in={true} key={isForgotPassword ? 'reset' : loginMode} timeout={300}>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5, fontSize: '1rem', fontFamily: '"Roboto Slab", serif' }}>
                                        {isForgotPassword ? 'Reset Password' : ''}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>
                                        {isForgotPassword
                                            ? 'Enter email for reset link.'
                                            : (loginMode === 'admin' ? 'Login with OTP' : 'Login with email')}
                                    </Typography>
                                </Box>
                            </Fade>
                        </Box>

                        <Box
                            component="form"
                            onSubmit={isForgotPassword ? handleResetPassword : (loginMode === 'admin' ? (otpStep === 'phone' ? handleSendOtp : handleVerifyOtp) : handleStaffLogin)}
                            sx={{ display: 'flex', flexDirection: 'column' }}
                        >
                            {/* FORM FIELDS CONTAINER - FIXED HEIGHT */}
                            <Box sx={{ position: 'relative', height: '150px' }}> {/* Reduced from 160px */}
                                <Fade in={true} key={isForgotPassword ? 'reset-form' : `form-${loginMode}`} timeout={300}>
                                    <Box sx={{ width: '100%', position: 'absolute', top: 0 }}>
                                        {isForgotPassword ? (
                                            <>
                                                <TextField
                                                    fullWidth
                                                    label="Email Address"
                                                    variant="outlined"
                                                    value={resetEmail}
                                                    onChange={(e) => { setResetEmail(e.target.value); if (error) setError(''); }}
                                                    disabled={loading}
                                                    error={!!error}
                                                    helperText={error || (resetSuccess ? 'Reset link sent!' : '')}
                                                    FormHelperTextProps={{ sx: { color: resetSuccess ? 'success.main' : 'error.main', fontWeight: 500, minHeight: '18px', fontSize: '0.65rem' } }}
                                                    InputLabelProps={{ shrink: true }}
                                                    InputProps={{ startAdornment: (<InputAdornment position="start"><EmailIcon sx={{ color: '#94a3b8', fontSize: 16 }} /></InputAdornment>) }}
                                                    sx={{ ...textFieldSx, mb: 2 }}
                                                />
                                                <Box display="flex" justifyContent="flex-end" sx={{ height: '26px', mt: 1 }}>
                                                    <Button variant="text" onClick={() => { setIsForgotPassword(false); setError(''); setResetSuccess(false); }} disabled={loading} sx={{ ...secondaryButtonSx, fontSize: '0.75rem' }}>
                                                        Back to Login
                                                    </Button>
                                                </Box>
                                            </>
                                        ) : (
                                            <>
                                                {loginMode === 'admin' && (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                                                        <TextField
                                                            fullWidth
                                                            label="Phone Number"
                                                            variant="outlined"
                                                            value={phoneNumber}
                                                            onChange={handlePhoneChange}
                                                            disabled={loading || otpStep === 'otp'}
                                                            error={!isPhoneValid}
                                                            helperText={phoneError}
                                                            InputLabelProps={{ shrink: true }}
                                                            InputProps={{
                                                                startAdornment: !phoneNumber.startsWith('+') && (
                                                                    <InputAdornment position="start">
                                                                        <PhoneIcon sx={{ color: '#94a3b8', fontSize: 16, mr: 0.5 }} />
                                                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#475569' }}>+91</Typography>
                                                                    </InputAdornment>
                                                                )
                                                            }}
                                                            inputProps={{
                                                                inputMode: 'tel',
                                                                pattern: '[+0-9]*',
                                                                maxLength: 16
                                                            }}
                                                            sx={{ ...textFieldSx, mb: 1.25 }}
                                                        />
                                                        {otpStep === 'otp' ? (
                                                            <TextField
                                                                fullWidth
                                                                label="OTP Code"
                                                                variant="outlined"
                                                                value={otp}
                                                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                                                disabled={loading}
                                                                InputLabelProps={{ shrink: true }}
                                                                sx={textFieldSx}
                                                            />
                                                        ) : (
                                                            <Box sx={{ height: '58px' }} />
                                                        )}
                                                    </Box>
                                                )}
                                                {loginMode === 'staff' && (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                        <TextField
                                                            fullWidth
                                                            label="Email Address"
                                                            variant="outlined"
                                                            value={email}
                                                            onChange={handleEmailChange}
                                                            disabled={loading}
                                                            error={!isEmailValid}
                                                            helperText={emailError}
                                                            InputLabelProps={{ shrink: true }}
                                                            InputProps={{ startAdornment: (<InputAdornment position="start"><EmailIcon sx={{ color: '#94a3b8', fontSize: 16 }} /></InputAdornment>) }}
                                                            sx={{ ...textFieldSx, mb: 2 }}
                                                        />
                                                        <TextField
                                                            fullWidth
                                                            label="Password"
                                                            type={showPassword ? 'text' : 'password'}
                                                            variant="outlined"
                                                            value={password}
                                                            onChange={handlePasswordChange}
                                                            disabled={loading}
                                                            error={!isPasswordValid}
                                                            helperText={passwordError}
                                                            InputLabelProps={{ shrink: true }}
                                                            InputProps={{
                                                                startAdornment: (<InputAdornment position="start"><LockIcon sx={{ color: '#94a3b8', fontSize: 16 }} /></InputAdornment>),
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small"><Visibility sx={{ color: '#94a3b8', fontSize: 16 }} /></IconButton>
                                                                    </InputAdornment>
                                                                )
                                                            }}
                                                            sx={textFieldSx}
                                                        />
                                                        <Box display="flex" justifyContent="flex-end" sx={{ height: '22px', mt: 0.5 }}>
                                                            <Button variant="text" onClick={() => setIsForgotPassword(true)} sx={{ ...secondaryButtonSx, fontSize: '0.75rem' }}>Forgot Password?</Button>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </>
                                        )}
                                    </Box>
                                </Fade>
                            </Box>

                            {/* Error Alert - FIXED HEIGHT CONTAINER */}
                            <Box sx={{ height: '22px', mt: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {error && !isForgotPassword && (
                                    <Fade in={true} sx={{ width: '100%' }}><Alert severity="error" sx={{ borderRadius: '8px', fontSize: '0.6rem', py: 0, '& .MuiAlert-icon': { alignItems: 'center', fontSize: '0.8rem' } }}>{error}</Alert></Fade>
                                )}
                            </Box>

                            <Button
                                fullWidth
                                type="submit"
                                variant="contained"
                                disabled={loading}
                                sx={{
                                    py: 0.7, // Reduced from 0.8
                                    borderRadius: '10px',
                                    background: 'linear-gradient(135deg, #0051C3 0%, #003d91 100%)',
                                    fontSize: '0.85rem', // Reduced from 0.9rem
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    boxShadow: '0 4px 14px rgba(0, 81, 195, 0.35)',
                                    mt: 1,
                                    mb: 2,
                                    '&.Mui-disabled': { background: 'linear-gradient(135deg, #0051C3 0%, #003d91 100%)', color: 'white', opacity: 0.7 }
                                }}
                            >
                                {loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : (isForgotPassword ? 'SEND RESET LINK' : (loginMode === 'admin' ? (otpStep === 'phone' ? 'Send OTP' : 'Verify & Sign In') : 'Login'))}
                            </Button>
                        </Box>
                    </Paper>
                </Fade>
            </Box>

            {/* Bottom section: ReviewCarousel on White Background */}
            <Box
                sx={{
                    flex: '0 0 auto',
                    minHeight: '35vh',
                    bgcolor: '#ffffff',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 2,
                    px: 1,
                    zIndex: 1,
                    boxShadow: '0 -4px 10px rgba(0,0,0,0.05)',
                    overflow: 'hidden' // Ensure no overflow
                }}
            >
                <ReviewCarousel compact={true} />
            </Box>

            {/* reCAPTCHA Container */}
            <div id="recaptcha-container" />
        </Box>
    );
};

export default MobileUnifiedLogin;
